import { useEffect, useMemo, useState } from 'react'
import { useWorkspace } from '../data/workspace'
import { subscribeBlocksInRange } from '../data/blocks'
import {
  COVERAGE_FLOOR,
  timeByFlag,
  categoryBreakdown,
  countedBlocks,
  excludedCategories,
  filterBlocks,
  fragmentation,
  interruptionSources,
  workBalance,
} from '../data/insights'
import { iterationDays, iterationsInRange } from '../data/iterations'
import { formatDayLabel, formatDuration } from '../lib/format'
import { dayRange, shiftDayKey, todayKey, weekStartKey, type DayKey } from '../lib/time'
import { ScopePicker } from './IterationsPage'
import type { Block } from '../data/types'

const RANGES = [
  { weeks: 1, label: 'This week' },
  { weeks: 4, label: '4 weeks' },
  { weeks: 12, label: '12 weeks' },
] as const

export function InsightsPage() {
  const { uid, categories, settings, iterations, taxonomy } = useWorkspace()
  const [weeks, setWeeks] = useState<number>(4)
  const [iterationId, setIterationId] = useState('')
  const [scope, setScope] = useState<string | null>(null)
  const [tag, setTag] = useState('')
  const [blocks, setBlocks] = useState<Block[]>([])

  const today = todayKey(settings.dayStartHour)

  /** Iterations near enough to be worth offering as a range. */
  const available = useMemo(
    () => iterationsInRange(iterations, shiftDayKey(today, -365), today),
    [iterations, today],
  )
  const chosen = available.find((iteration) => iteration.id === iterationId) ?? null

  const days = useMemo(() => {
    // An iteration that has not finished yet only reports the part that has.
    if (chosen) return iterationDays(chosen).filter((day) => day <= today)
    const firstMonday = shiftDayKey(weekStartKey(today), -(weeks - 1) * 7)
    const list: DayKey[] = []
    for (let cursor = firstMonday; cursor <= today; cursor = shiftDayKey(cursor, 1)) list.push(cursor)
    return list
  }, [chosen, weeks, today])

  const range = useMemo(() => {
    const first = days[0] as DayKey
    const last = days[days.length - 1] as DayKey
    return { start: dayRange(first, settings.dayStartHour).start, end: dayRange(last, settings.dayStartHour).end }
  }, [days, settings.dayStartHour])

  useEffect(
    () => subscribeBlocksInRange(uid, range.start, range.end, setBlocks),
    [uid, range.start, range.end],
  )

  const tags = useMemo(
    () => [...new Set(blocks.flatMap((block) => block.tags))].sort(),
    [blocks],
  )

  // Everything below is about counted time: what a flag has been marked out of
  // Insights never reaches any of it.
  const counted = useMemo(
    () => countedBlocks(blocks, excludedCategories(categories)),
    [blocks, categories, taxonomy],
  )

  const filtered = useMemo(
    () => filterBlocks(counted, { tag: tag || null }),
    [counted, tag],
  )

  const frag = fragmentation(filtered, categories, scope)
  const byFlag = timeByFlag(filtered, categories)
  const interruptions = interruptionSources(filtered, categories, scope)
  const breakdown = categoryBreakdown(filtered, categories)
  const balance = workBalance(counted, days, settings, today)


  return (
    <div className="mx-auto flex w-full max-w-3xl lg:max-w-5xl min-h-0 flex-1 flex-col gap-3 overflow-y-auto overscroll-contain px-4 pt-3 pb-6">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-lg border border-line p-0.5">
          {RANGES.map((option) => (
            <button
              key={option.weeks}
              type="button"
              onClick={() => {
                setWeeks(option.weeks)
                setIterationId('')
              }}
              aria-pressed={!chosen && weeks === option.weeks}
              className={`rounded-md px-2.5 py-1 text-[13px] font-medium transition-colors duration-150 ${
                !chosen && weeks === option.weeks ? 'bg-raised text-text' : 'text-dim hover:text-text'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
        {available.length > 0 ? (
          <select
            value={iterationId}
            onChange={(event) => setIterationId(event.target.value)}
            aria-label="Iteration"
            className={`rounded-lg border border-line bg-bg px-2 py-1 text-[13px] outline-none focus:border-line-strong ${
              iterationId ? 'text-text' : 'text-dim'
            }`}
          >
            <option value="">By iteration…</option>
            {[...available].reverse().map((iteration) => (
              <option key={iteration.id} value={iteration.id}>
                {iteration.name}
              </option>
            ))}
          </select>
        ) : null}
        <Picker value={tag} onChange={setTag} options={tags} placeholder="All tags" />
        <ScopePicker flags={taxonomy?.flags ?? []} scope={scope} onChange={setScope} />
        <span className="text-[12px] text-faint tnum">
          {formatDayLabel(days[0] as DayKey, 'd MMM')} – {formatDayLabel(days[days.length - 1] as DayKey, 'd MMM')}
        </span>
      </div>

      {!balance.trustworthy ? (
        <p className="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-[13px] leading-relaxed text-danger">
          The finished days in this range hold {Math.round(balance.ratio * 100)}% of the{' '}
          {settings.dailyTargetHours}h they were meant to. That is either untracked time or
          genuinely lighter days; below {Math.round(COVERAGE_FLOOR * 100)}% the figures are not
          worth reading too closely either way.
        </p>
      ) : null}

      <Card
        title="Fragmentation"
        hint={
          scope === null
            ? 'Uninterrupted stretches of tracked time. Pick a flag above to narrow it to the work you actually care about being uninterrupted.'
            : 'Uninterrupted stretches of time on categories carrying this flag. A run of them with nothing else between counts once.'
        }
      >
        <div className="flex flex-wrap items-end gap-x-8 gap-y-3">
          <Figure value={formatDuration(frag.median)} label="median stretch" big />
          <Figure value={formatDuration(frag.longest)} label="longest" />
          <Figure value={String(frag.durations.length)} label="stretches" />
          <Figure value={formatDuration(frag.total)} label="in scope" />
        </div>
        <Histogram buckets={frag.buckets} />
      </Card>

      <Card
        title="Time by flag"
        hint="Tracked time by the flags on the categories that produced it. Tag some categories and the comparison you care about appears here."
      >
        {byFlag.slices.length === 0 ? (
          <Empty>Nothing tracked in this range.</Empty>
        ) : (
          <Bars
            rows={byFlag.slices.map((slice) => ({
              label: slice.flagId
                ? (taxonomy?.flags.find((flag) => flag.id === slice.flagId)?.label ?? slice.flagId)
                : 'no flag',
              value: slice.ms,
              color: slice.flagId ? 'var(--tg-accent)' : 'var(--tg-line-strong)',
              suffix: `${formatDuration(slice.ms)} · ${Math.round(slice.share * 100)}%`,
            }))}
          />
        )}
      </Card>

      <Card title="Interruption sources" hint="What most often ends a run of the time in scope.">
        {interruptions.length === 0 ? (
          <Empty>Nothing has interrupted a stretch yet.</Empty>
        ) : (
          <Bars
            rows={interruptions.map((entry) => ({
              label: categories.get(entry.categoryId)?.shortLabel ?? entry.categoryId,
              value: entry.count,
              color: categories.get(entry.categoryId)?.color ?? 'var(--tg-line-strong)',
              suffix: `${entry.count}×`,
            }))}
            format={(value) => `${value}×`}
          />
        )}
      </Card>

      <Card title="Where the time goes">
        {breakdown.length === 0 ? (
          <Empty>Nothing tracked in this range.</Empty>
        ) : (
          <Bars
            rows={breakdown.map((entry) => ({
              label: categories.get(entry.categoryId)?.shortLabel ?? entry.categoryId,
              value: entry.ms,
              color: categories.get(entry.categoryId)?.color ?? 'var(--tg-line-strong)',
            }))}
          />
        )}
      </Card>

      <Card
        title="Work balance"
        hint={`Counted time against ${settings.dailyTargetHours}h a day, added up rather than judged one day at a time — a short day and a long one cancel out. Today is left out until it is over.`}
      >
        <div className="flex flex-wrap items-end gap-x-8 gap-y-3">
          <Figure
            value={`${balance.balance >= 0 ? '+' : '−'}${formatDuration(Math.abs(balance.balance))}`}
            label={balance.balance >= 0 ? 'banked' : 'owed'}
            big
          />
          <Figure value={formatDuration(balance.counted)} label="counted" />
          <Figure
            value={formatDuration(balance.target)}
            label={`expected over ${balance.days.length} days`}
          />
          <Figure
            value={`${formatDuration(balance.today)}${balance.todayTarget > 0 ? ` / ${formatDuration(balance.todayTarget)}` : ''}`}
            label="today so far"
          />
        </div>
        <DayBars days={balance.days} />
      </Card>
    </div>
  )
}

/** One bar per finished day, against the line they were all meant to reach. */
function DayBars({ days }: { days: Array<{ day: DayKey; counted: number; target: number }> }) {
  if (days.length === 0) return <Empty>No finished days in this range yet.</Empty>
  const recent = days.slice(-45)
  const target = recent.reduce((max, entry) => Math.max(max, entry.target), 0)
  const peak = recent.reduce((max, entry) => Math.max(max, entry.counted), target || 1)
  const height = 76

  return (
    <div className="relative pt-4" style={{ height: height + 16 }}>
      {target > 0 ? (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 border-t border-dashed border-line-strong"
          style={{ bottom: (target / peak) * height }}
        />
      ) : null}
      <div className="flex h-full items-end gap-[3px]">
        {recent.map((entry) => (
          <span
            key={entry.day}
            title={`${formatDayLabel(entry.day, 'EEE d MMM')} — ${formatDuration(entry.counted)}${
              entry.target > 0 ? ` of ${formatDuration(entry.target)}` : ' (no target)'
            }`}
            className="min-w-0 flex-1 rounded-t-sm"
            style={{
              height: `${Math.max(2, (entry.counted / peak) * height)}px`,
              backgroundColor:
                entry.target > 0 && entry.counted < entry.target
                  ? 'var(--tg-line-strong)'
                  : 'var(--tg-accent)',
            }}
          />
        ))}
      </div>
    </div>
  )
}

function Card({
  title,
  hint,
  children,
}: {
  title: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <section className="rounded-xl border border-line bg-surface p-3">
      <h2 className="text-[14px] font-semibold">{title}</h2>
      {hint ? <p className="pt-0.5 pb-2 text-[12px] leading-relaxed text-faint">{hint}</p> : <div className="pb-2" />}
      {children}
    </section>
  )
}

function Figure({ value, label, big = false }: { value: string; label: string; big?: boolean }) {
  return (
    <div>
      <p className={`${big ? 'text-[28px]' : 'text-[20px]'} leading-none font-semibold tracking-tight tnum`}>
        {value}
      </p>
      <p className="pt-1 text-[12px] leading-tight text-faint">{label}</p>
    </div>
  )
}

function Histogram({ buckets }: { buckets: Array<{ label: string; count: number }> }) {
  const max = buckets.reduce((peak, bucket) => Math.max(peak, bucket.count), 0)
  if (max === 0) return <Empty>No implementation stretches in this range.</Empty>
  return (
    <div className="flex items-end gap-1.5 pt-4">
      {buckets.map((bucket) => (
        <div key={bucket.label} className="flex flex-1 flex-col items-center gap-1">
          <span className="text-[11px] text-faint tnum">{bucket.count || ''}</span>
          <div
            className="w-full rounded-t bg-accent/70"
            style={{ height: `${Math.max(2, (bucket.count / max) * 64)}px` }}
          />
          <span className="text-[11px] whitespace-nowrap text-faint">{bucket.label}</span>
        </div>
      ))}
    </div>
  )
}

function Bars({
  rows,
  format = formatDuration,
}: {
  rows: Array<{ label: string; value: number; color: string; suffix?: string }>
  format?: (value: number) => string
}) {
  const max = rows.reduce((peak, row) => Math.max(peak, row.value), 0)
  if (max === 0) return <Empty>Nothing to show yet.</Empty>
  return (
    <ul className="flex flex-col gap-1.5 pt-2">
      {rows.map((row) => (
        <li key={row.label} className="flex items-center gap-2">
          <span className="w-20 shrink-0 truncate text-[12px] text-dim">{row.label}</span>
          <span className="h-3 min-w-0 flex-1 overflow-hidden rounded-sm bg-line">
            <span
              className="block h-full rounded-sm"
              style={{ width: `${(row.value / max) * 100}%`, backgroundColor: row.color }}
            />
          </span>
          <span className="w-16 shrink-0 text-right text-[12px] text-faint tnum">
            {row.suffix ?? format(row.value)}
          </span>
        </li>
      ))}
    </ul>
  )
}

function Picker({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string
  onChange: (value: string) => void
  options: string[]
  placeholder: string
}) {
  if (options.length === 0) return null
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={`rounded-lg border border-line bg-bg px-2 py-1 text-[13px] outline-none focus:border-line-strong ${
        value ? 'text-text' : 'text-dim'
      }`}
    >
      <option value="">{placeholder}</option>
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  )
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="py-2 text-[13px] text-faint">{children}</p>
}
